import { Box, Table, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable/CustomTable";
import {
  SALES_ORDER_STATUS_COLOR,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import { ProfitReportItem, ProfitReportRow } from "constants/types/report";
import { MRT_ColumnDef, MRT_TableOptions } from "mantine-react-table";
import { FC, useMemo } from "react";
import { formatCurrency, formatDate } from "utils/helpers/format";

interface ProfitReportTableProps {
  data: ProfitReportRow[];
}

const ProfitCell: FC<{ value: number }> = ({ value }) => (
  <Text
    c={value > 0 ? "green" : value < 0 ? "red" : undefined}
    fw={500}
    size="sm"
  >
    {formatCurrency(value)}
  </Text>
);

interface ProfitItemsTableProps {
  items: ProfitReportItem[];
  orderIndex: number;
}

const ProfitItemsTable: FC<ProfitItemsTableProps> = ({ items, orderIndex }) => {
  if (!items.length) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xs">
        Chưa có mặt hàng nào.
      </Text>
    );
  }

  return (
    <Table.ScrollContainer minWidth={980}>
      <Table
        striped
        withColumnBorders
        withRowBorders
        verticalSpacing={6}
        horizontalSpacing="xs"
        fz="sm"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta="center" w={70}>
              STT
            </Table.Th>
            <Table.Th>Tên mặt hàng</Table.Th>
            {/* <Table.Th w={120}>Đơn vị</Table.Th> */}
            <Table.Th ta="right" w={90}>
              Số lượng
            </Table.Th>
            <Table.Th ta="right" w={130}>
              Đơn giá
            </Table.Th>
            <Table.Th ta="right" w={150}>
              Số tiền trước thuế
            </Table.Th>
            <Table.Th ta="right" w={120}>
              Thuế
            </Table.Th>
            <Table.Th ta="right" w={140}>
              Tổng cộng
            </Table.Th>
            <Table.Th ta="right" w={130}>
              Giá net
            </Table.Th>
            <Table.Th ta="right" w={140}>
              Lợi nhuận
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item, itemIndex) => (
            <Table.Tr key={item.itemId}>
              <Table.Td ta="center">{`${orderIndex}.${itemIndex + 1}`}</Table.Td>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {item.productDisplayName || item.productName}
                </Text>
                <Text size="xs" c="dimmed">
                  {item.productCode}
                </Text>
                {item.productSpec && (
                  <Text size="xs" c="dimmed">
                    {item.productSpec}
                  </Text>
                )}
              </Table.Td>
              {/* <Table.Td>{item.productUnit}</Table.Td> */}
              <Table.Td ta="right">{item.quantity}</Table.Td>
              <Table.Td ta="right">{formatCurrency(item.unitPrice)}</Table.Td>
              <Table.Td ta="right">
                {formatCurrency(item.amountBeforeTax)}
              </Table.Td>
              <Table.Td ta="right">{formatCurrency(item.taxAmount)}</Table.Td>
              <Table.Td ta="right">{formatCurrency(item.totalAmount)}</Table.Td>
              <Table.Td ta="right">{formatCurrency(item.priceNet)}</Table.Td>
              <Table.Td ta="right">
                <ProfitCell value={item.profit} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

const ProfitReportTable: FC<ProfitReportTableProps> = ({ data }) => {
  const columns = useMemo<MRT_ColumnDef<ProfitReportRow>[]>(
    () => [
      {
        id: "stt",
        header: "STT",
        size: 50,
        grow: false,
        Cell: ({ row }) => row.index + 1,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
      },
      { accessorKey: "orderCode", header: "Mã đơn hàng", size: 150 },
      { accessorKey: "partnerName", header: "Khách hàng", size: 200 },
      {
        accessorKey: "contactPerson.fullname",
        header: "Người phụ trách",
        size: 180,
        Cell: ({ row }) => row.original.contactPerson?.fullname ?? "—",
      },
      {
        accessorKey: "orderCreatedAt",
        header: "Ngày tạo",
        size: 150,
        Cell: ({ cell }) => formatDate(cell.getValue<number>()),
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 160,
        grow: false,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <CustomBadge
              label={SALES_ORDER_STATUS_LABEL[status] ?? status}
              color={SALES_ORDER_STATUS_COLOR[status] ?? "gray"}
            />
          );
        },
      },
      {
        accessorKey: "totalRevenue",
        header: "Doanh thu",
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      },
      {
        accessorKey: "totalCost",
        header: "Tổng giá Net",
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      },
      {
        accessorKey: "grossProfit",
        header: "Lợi nhuận",
        size: 150,
        Cell: ({ cell }) => <ProfitCell value={cell.getValue<number>()} />,
      },
      {
        accessorKey: "paidAmount",
        header: "Đã thu",
        size: 140,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      },
      {
        accessorKey: "remainingAmount",
        header: "Còn lại",
        size: 140,
        Cell: ({ cell }) => {
          const v = cell.getValue<number>();
          return (
            <Text c={v > 0 ? "orange" : undefined} size="sm">
              {formatCurrency(v)}
            </Text>
          );
        },
      },
      {
        accessorKey: "realizedProfit",
        header: "Lợi nhuận thực thu",
        size: 160,
        Cell: ({ cell }) => <ProfitCell value={cell.getValue<number>()} />,
      },
    ],
    [],
  );

  const mrtOptions = useMemo<Partial<MRT_TableOptions<ProfitReportRow>>>(
    () => ({
      displayColumnDefOptions: {
        "mrt-row-expand": { size: 40, grow: false },
      },
      mantineExpandButtonProps: ({ row }) => ({
        children: (
          <IconChevronRight
            size={16}
            style={{
              transform: row.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        ),
      }),
      mantineDetailPanelProps: {
        style: { padding: 4 },
      },
      renderDetailPanel: ({ row }) => (
        <Box px="xs" py={6}>
          <ProfitItemsTable
            items={row.original.items ?? []}
            orderIndex={row.index + 1}
          />
        </Box>
      ),
    }),
    [],
  );

  return <CustomTable columns={columns} data={data} mrtOptions={mrtOptions} />;
};

export default ProfitReportTable;
